<template>
  <div class="bank-data-row">
    <SelectMenue
      :label="$t('changeBankDataForm.accountHolder')"
      :options="accountHolderOptions"
      :required="true"
      :value="selectedAccountHolder"
      class="bank-data-select"
      @update:input="handleAccountHolderUpdate"
    />
  </div>

  <NameInput
    v-if="isNameInputNeeded()"
    :nameDate="newNameData"
    @input="updateNameInput"
    @input:status="updateNameInputStatus"
  ></NameInput>

  <AddressInput
    v-if="isAddressInputNeeded()"
    :cityInfo="$t('addressDetails.cityInfo')"
    :cityLabel="$t('addressDetails.cityLabel')"
    :postCodeLabel="$t('addressDetails.postCodeLabel')"
    :postalServiceUrl="postalServiceURL()"
    :postalServiceUrlApiKey="postalServiceApiKey()"
    :streetInfo="$t('addressDetails.streetInfo')"
    :streetLabel="$t('addressDetails.streetLabel')"
    :streetNumberInfo="$t('addressDetails.streetNumberInfo')"
    :value="newAddressData"
    @input="updateAddressInput"
    @input:status="updateAddressInputStatus"
  ></AddressInput>

  <div class="bank-data-row">
    <BasicTextfield
      :label="$t('changeBankDataForm.newIban')"
      :required="true"
      :status="ibanStatus"
      :value="changeBankDataRequest.newIban"
      class="bank-data-text bank-data-iban"
      @input="(e) => validateIban(e)"
      @update:input="validateIban"
      @update:error="validateIban"
      @update:cleared="validateIban"
    >
      <template v-slot:error>
        <span v-if="ibanErrorKey">{{ $t(ibanErrorKey) }}</span>
        <span v-else>{{ $t('changeBankDataForm.enterNewIban') }}</span>
      </template>
    </BasicTextfield>

    <BasicTextfield
      :label="$t('changeBankDataForm.newBic')"
      :status="bicStatus"
      :value="changeBankDataRequest.newBic"
      class="bank-data-text bank-data-bic"
      @input="validateBic"
      @update:input="validateBic"
      @update:error="validateBic"
      @update:cleared="validateBic"
    >
      <template v-slot:error>
        <span v-if="bicErrorKey">{{ $t(bicErrorKey) }}</span>
        <span v-else>{{ $t('changeBankDataForm.enterNewBic') }}</span>
      </template>
    </BasicTextfield>

    <BasicTextfield
      :label="$t('changeBankDataForm.newBankingInstitute')"
      :required="true"
      :status="bankInstituteStatus"
      :value="changeBankDataRequest.newBankingInstitute"
      class="bank-data-text bank-data-bank-institute"
      @input="validateBankInstitute"
      @update:input="validateBankInstitute"
      @update:error="validateBankInstitute"
      @update:cleared="validateBankInstitute"
    >
      <template v-slot:error>{{
        $t('changeBankDataForm.enterNewBankingInstitute')
      }}</template>
    </BasicTextfield>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, type PropType, reactive, type Ref, ref, watch } from 'vue'
import {
  type AddressData,
  AddressInput,
  BasicTextfield,
  InputStatus,
  type NameData,
  NameInput,
  SelectMenue
} from 'de-zurich-ui-library'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import dayjs from 'dayjs'
import type { ChangeBankDataRequest, Policy } from 'policies-api'
// Account holder options now represented as array of { label, value }
import { getPartnerProxyId, getToken } from '@/utils/KeycloakUtils'
import { formatIban, isValidIbanForCountry, stripIban } from '@/utils/iban'
import { navigate } from '@/utils/NavigationUtils'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

dayjs.extend(customParseFormat)

const props = defineProps({
  policies: {
    type: Object as PropType<Policy[]>,
    required: true
  },
  formStartDate: {
    type: String,
    required: true
  }
})

const emit = defineEmits<{
  formLoading: [value: boolean]
  formSubmitDisabled: [value: boolean]
  showErrorTile: [value: boolean]
  formResetButton: [none: void]
}>()

const partnerProxyId = ref('')
const loginRedirectUrl = ref(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)

const newAddressData: Ref<AddressData> = ref(getEmptyNewAddressData())
const newNameData: Ref<NameData> = ref(getEmptyNewNameData())
interface AccountHolderOption {
  label: string
  value: string
}
const accountHolderOptions: AccountHolderOption[] = [
  {
    value: 'policyHolder',
    label: t('changeBankDataForm.accountHolderOptions.policyHolder').toString()
  },
  {
    value: 'otherPersonOtherAddress',
    label: t('changeBankDataForm.accountHolderOptions.otherPersonOtherAddress').toString()
  },
  {
    value: 'otherPersonSameAddress',
    label: t('changeBankDataForm.accountHolderOptions.otherPersonSameAddress').toString()
  }
]
const defaultAccountHolder = ref('policyHolder')
const selectedAccountHolder = ref(defaultAccountHolder.value)

const changeBankDataRequest: ChangeBankDataRequest = reactive({
  id: '',
  newIban: '',
  newBic: '',
  newBankingInstitute: '',
  newSalutation: '',
  newTitle: '',
  newFirstName: '',
  newName: '',
  newPostCode: '',
  newCity: '',
  newStreet: '',
  newStreetNumber: '',
  newStreetExtra: '',
  associatedPolicyIds: [],
  startDate: ''
})

// validation status
const ibanStatus: Ref<InputStatus> = ref<InputStatus>(InputStatus.DEFAULT)
const bicStatus: Ref<InputStatus> = ref<InputStatus>(InputStatus.DEFAULT)
const bankInstituteStatus: Ref<InputStatus> = ref<InputStatus>(InputStatus.DEFAULT)
const addressInputStatus: Ref<InputStatus> = ref<InputStatus>(InputStatus.DEFAULT)
const nameInputStatus: Ref<InputStatus> = ref<InputStatus>(InputStatus.DEFAULT)
const ibanErrorKey: Ref<string> = ref('')
const bicTouched: Ref<boolean> = ref(false)
const bicErrorKey: Ref<string> = ref('')

const updateNameInput = (input: NameData) => {
  newNameData.value = input
}

interface BankverbindungReturn {
  bankinstitut: {
    $: string
  }
  bic: {
    $: string
  }
}

onMounted(() => {
  const t_partnerProxyId = getPartnerProxyId()
  if (!t_partnerProxyId) {
    throw new Error('No partner proxy id available!')
  }
  partnerProxyId.value = t_partnerProxyId

  changeBankDataRequest.id = partnerProxyId.value
  emit('formLoading', false)
})

// FUNCTIONS

function handleAccountHolderUpdate(...args: unknown[]): void {
  // SelectMenue usually emits the selected value as the first argument
  const val = args[0]
  selectedAccountHolder.value = val as string
}

function getEmptyNewAddressData(): AddressData {
  return {
    postCode: '',
    city: '',
    street: '',
    streetNumber: '',
    streetExtra: ''
  }
}

function getEmptyNewNameData(): NameData {
  return {
    salutation: '',
    title: '',
    firstName: '',
    name: ''
  }
}

function isNameInputNeeded(): boolean {
  return selectedAccountHolder.value !== 'policyHolder'
}

function isAddressInputNeeded(): boolean {
  return selectedAccountHolder.value === 'otherPersonOtherAddress'
}

function postalServiceURL(): string {
  return globalThis.ENV_CONFIG?.SERVICE_HOSTS.POSTAL
}

function postalServiceApiKey(): string {
  return globalThis.ENV_CONFIG?.API_KEY
}

function isDateValid(date: string): boolean {
  let useableDate = new Date(date)
  if (date.toString().match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
    useableDate = new Date(date.toString().split('.').reverse().join('/'))
  }
  const today = dayjs()
  const todayMinusOneDay = today.subtract(1, 'day')
  const todayPlusOneYear = today.add(1, 'year').add(1, 'day')
  const inputDate = dayjs(useableDate)
  return (
    inputDate.isValid() &&
    inputDate.isAfter(todayMinusOneDay, 'date') &&
    inputDate.isBefore(todayPlusOneYear, 'date')
  )
}

defineExpose({ formSubmit })

async function formSubmit(): Promise<void> {
  clearUnwantedData()

  if (!isDateValid(props.formStartDate)) {
    emit('formResetButton')
    return
  }

  changeBankDataRequest.startDate = props.formStartDate
  changeBankDataRequest.id = partnerProxyId.value
  changeBankDataRequest.newSalutation = newNameData.value.salutation
  changeBankDataRequest.newTitle = newNameData.value.title
  changeBankDataRequest.newFirstName = newNameData.value.firstName
  changeBankDataRequest.newName = newNameData.value.name
  changeBankDataRequest.newPostCode = newAddressData.value.postCode
  changeBankDataRequest.newCity = newAddressData.value.city
  changeBankDataRequest.newStreet = newAddressData.value.street
  changeBankDataRequest.newStreetNumber = newAddressData.value.streetNumber
  changeBankDataRequest.newStreetExtra = newAddressData.value.streetExtra
  changeBankDataRequest.associatedPolicyIds = props.policies.map(
    (policy) => policy.policyId
  )
  changeBankDataRequest.newIban =
    changeBankDataRequest.newIban?.replaceAll(/\s/g, '') || ''

  try {
    const accessToken = await getToken()
    const url = `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POLICIES}/self-services/change-bank-data`
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      KeyId: globalThis.ENV_CONFIG?.API_KEY,
      'Content-Type': 'application/json'
    }

    fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(changeBankDataRequest)
    }).then((response: Response) => {
      if (response.ok) {
        navigate('/danke?bankdaten-aendern')
      } else {
        emit('showErrorTile', true)
      }
    })
  } catch {
    emit('showErrorTile', true)
  }
}

watch(
  [ibanStatus, bicStatus, bankInstituteStatus, nameInputStatus, addressInputStatus],
  () => {
    let isFormInvalid =
      ibanStatus.value !== InputStatus.VALID ||
      bicStatus.value !== InputStatus.VALID ||
      bankInstituteStatus.value !== InputStatus.VALID

    if (selectedAccountHolder.value === 'otherPersonSameAddress') {
      isFormInvalid = isFormInvalid || nameInputStatus.value !== InputStatus.VALID
    }

    if (selectedAccountHolder.value === 'otherPersonOtherAddress') {
      isFormInvalid =
        isFormInvalid ||
        nameInputStatus.value !== InputStatus.VALID ||
        addressInputStatus.value !== InputStatus.VALID
    }

    emit('formSubmitDisabled', isFormInvalid)
    return isFormInvalid
  }
)

function clearUnwantedData(): void {
  if (!isNameInputNeeded()) {
    newNameData.value = getEmptyNewNameData()
  }
  if (!isAddressInputNeeded()) {
    newAddressData.value = getEmptyNewAddressData()
  }
}

function getIbanCountryCode(): string {
  return changeBankDataRequest.newIban?.substring(0, 2) || ''
}

function fetchBicAndBankname(iban: string) {
  getToken().then((accessToken) => {
    const url = `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.IBAN}/validate?IBAN=${iban}`
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      KeyId: globalThis.ENV_CONFIG?.API_KEY,
      'Content-Type': 'application/json'
    }

    fetch(url, {
      method: 'GET',
      headers: headers
    })
      .then(async (response) => {
        const respData = await response.json()
        const bankData = respData.validateBankVerbindungResponse.return
          .bankverbindungReturn as BankverbindungReturn
        if (bankData && bankData.bic && bankData.bic.$) {
          // Only populate BIC if the user hasn't touched the BIC field.
          if (!bicTouched.value) {
            changeBankDataRequest.newBic = bankData.bic.$
            validateBic(null)
          }
        }
        if (bankData && bankData.bankinstitut && bankData.bankinstitut.$) {
          // Only populate bank institute if status is still DEFAULT or field is empty
          if (
            bankInstituteStatus.value === InputStatus.DEFAULT ||
            !changeBankDataRequest.newBankingInstitute
          ) {
            changeBankDataRequest.newBankingInstitute = bankData.bankinstitut.$
            validateBankInstitute(null)
          }
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          // Unauthenticated call, redirect to login page
          navigate(loginRedirectUrl.value)
        } else {
          // no specification
        }
      })
  })
}

// Helper types
type IbanInputArg = string | Event | null

// Helper: parse input arg and compute commonly used values
function parseIbanInput(arg: IbanInputArg, maybeEvent?: Event) {
  let event: Event | undefined = undefined
  if (arg instanceof Event) {
    event = arg
    arg = null
  } else if (maybeEvent instanceof Event) {
    event = maybeEvent
  }

  const target = event && (event.target as HTMLInputElement | null)
  let selectionStart: number | null = null
  let selectionEnd: number | null = null
  let prevInputValue: string | null = null
  if (target) {
    selectionStart = target.selectionStart
    selectionEnd = target.selectionEnd
    prevInputValue = target.value
  }

  if (arg !== null && typeof arg === 'string') {
    changeBankDataRequest.newIban = arg
  }

  const stripped = stripIban(changeBankDataRequest.newIban || '')
  const formatted = formatIban(stripped)
  const maxInsertedSpaces = Math.ceil(34 / 4)
  const formattedTruncated = formatted.substring(0, 34 + maxInsertedSpaces)

  return {
    event,
    target,
    selectionStart,
    selectionEnd,
    prevInputValue,
    stripped,
    formattedTruncated
  }
}

function validateIbanParts(
  stripped: string,
  formattedTruncated: string,
  countryCode: string,
  event?: Event,
  target?: HTMLInputElement | null
) {
  if (!countryCode.match(/[A-Z]{2}/g)) {
    if (changeBankDataRequest.newIban && changeBankDataRequest.newIban.trim() !== '') {
      ibanStatus.value = InputStatus.ERROR
      ibanErrorKey.value = 'changeBankDataForm.ibanInvalidCountry'
    } else {
      ibanStatus.value = InputStatus.ERROR
      ibanErrorKey.value = ''
    }
    return false
  }

  if (stripped !== formattedTruncated.replaceAll(/\s/g, '').toUpperCase()) {
    ibanStatus.value = InputStatus.ERROR
    ibanErrorKey.value = 'changeBankDataForm.ibanInvalidChars'
    if (event && target) target.value = formattedTruncated
    return false
  }

  if (stripped.length < 15 || stripped.length > 34) {
    ibanStatus.value = InputStatus.ERROR
    ibanErrorKey.value = 'changeBankDataForm.ibanInvalidLength'
    return false
  }

  if (countryCode === 'DE') {
    if (stripped.length !== 22) {
      ibanStatus.value = InputStatus.ERROR
      ibanErrorKey.value = 'changeBankDataForm.ibanInvalidLengthDE'
      return false
    }
    if (!isValidIbanForCountry(stripped)) {
      ibanStatus.value = InputStatus.ERROR
      ibanErrorKey.value = 'changeBankDataForm.ibanInvalidChecksum'
      return false
    }
  }

  return true
}

function validateIban(valOrEvent: IbanInputArg, maybeEvent?: Event): void {
  const parsed = parseIbanInput(valOrEvent, maybeEvent)
  const {
    event,
    target,
    selectionStart,
    selectionEnd,
    prevInputValue,
    stripped,
    formattedTruncated
  } = parsed

  // If IBAN is empty, clear BIC and bank institute fields
  if (stripped.length === 0) {
    changeBankDataRequest.newBic = ''
    changeBankDataRequest.newBankingInstitute = ''
    bicStatus.value = InputStatus.DEFAULT
    bankInstituteStatus.value = InputStatus.ERROR
    ibanStatus.value = InputStatus.ERROR
    ibanErrorKey.value = ''
    changeBankDataRequest.newIban = ''
    return
  }

  const countryCode = getIbanCountryCode()

  if (!validateIbanParts(stripped, formattedTruncated, countryCode, event, target)) return

  // Fetch BIC and bank name for valid DE IBANs
  if (countryCode === 'DE' && stripped.length === 22) {
    fetchBicAndBankname(stripped)
  }

  ibanStatus.value = InputStatus.VALID
  ibanErrorKey.value = ''
  validateBic(null)

  if (event && target && prevInputValue !== null && selectionStart !== null) {
    try {
      const rawBeforePrev = stripIban(prevInputValue.slice(0, selectionStart))
      const newStart = formatIban(rawBeforePrev).length
      const selectionLength =
        (selectionEnd === null ? 0 : selectionEnd - selectionStart) || 0

      target.value = formattedTruncated

      setTimeout(() => {
        try {
          target.setSelectionRange(newStart, newStart + selectionLength)
        } catch (e) {
          console.debug('validateIban setSelectionRange failed', e)
        }
        changeBankDataRequest.newIban = target.value
      }, 0)
      return
    } catch (err) {
      console.debug('validateIban fast-path failed', err)
    }
  }

  changeBankDataRequest.newIban = formattedTruncated
}

function validateBic(valOrEvent: string | Event | null, maybeEvent?: Event): void {
  // support being called with (value) or (event) from the native input
  let event: Event | undefined = undefined
  if (valOrEvent instanceof Event) {
    event = valOrEvent
    valOrEvent = null
  } else if (maybeEvent instanceof Event) {
    event = maybeEvent
  }

  // if the user interacted via keyboard/input, mark as touched so fetch doesn't overwrite
  if (event) bicTouched.value = true

  if (valOrEvent !== null && typeof valOrEvent === 'string') {
    changeBankDataRequest.newBic = valOrEvent
  }

  // normalize: remove whitespace and uppercase (do NOT truncate here so user can see errors >11)
  changeBankDataRequest.newBic =
    (changeBankDataRequest.newBic || '').replaceAll(/\s/g, '').toUpperCase() || ''

  const bic = changeBankDataRequest.newBic
  const countryCode = getIbanCountryCode()

  // If no BIC provided: required for non-DE IBANs, optional for DE
  if (!bic || bic.length === 0) {
    bicStatus.value = countryCode === 'DE' ? InputStatus.VALID : InputStatus.ERROR
    bicErrorKey.value = countryCode === 'DE' ? '' : 'changeBankDataForm.bicInvalidLength'
    return
  }

  // If BIC provided, must be 8 or 11 characters (allow 8..11)
  if (bic.length < 8 || bic.length > 11) {
    bicStatus.value = InputStatus.ERROR
    bicErrorKey.value = 'changeBankDataForm.bicInvalidLength'
    return
  }

  bicStatus.value = InputStatus.VALID
  bicErrorKey.value = ''
}

function validateBankInstitute(val: string | null): void {
  if (val !== null && val !== undefined) changeBankDataRequest.newBankingInstitute = val
  // Remove specific unwanted special characters but keep valid characters
  changeBankDataRequest.newBankingInstitute =
    changeBankDataRequest.newBankingInstitute
      ?.replaceAll(/[_{}<>"[\]]/g, '')
      ?.trim()
      ?.substring(0, 58) || ''
  bankInstituteStatus.value =
    changeBankDataRequest.newBankingInstitute === ''
      ? InputStatus.ERROR
      : InputStatus.VALID
}

function updateNameInputStatus(value: InputStatus) {
  nameInputStatus.value = value
}

const updateAddressInput = (input: AddressData) => {
  newAddressData.value = input
}

function updateAddressInputStatus(value: InputStatus) {
  addressInputStatus.value = value
}
</script>

<style lang="scss" scoped>
.bank-data-row {
  display: flex;

  .bank-data-text {
    margin-right: 32px;
  }

  .bank-data-iban {
    width: 35%;
  }

  .bank-data-bic {
    width: 25%;
  }

  .bank-data-bank-institute {
    width: 45%;
    margin-right: 0;
  }
}

@media only screen and (max-width: 600px) {
  .bank-data-row {
    flex-direction: column;
    .bank-data-text {
      width: 100%;
    }
    .bank-data-select {
      width: 100%;
    }
  }
}
</style>
